import React, { useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  generateInstallments, 
  validateAgreementParamsWithTimezone,
  formatCurrencyForDisplay,
  formatDateForDisplay,
  type InstallmentCalculationParams,
  type InstallmentCalculationResult
} from '@kist/shared';
import { Calendar, Calculator, FileText, CreditCard, AlertCircle, CheckCircle, PenTool } from 'lucide-react';
import toast from 'react-hot-toast';
import { SignatureCapture } from './SignatureCapture';

// Zod schema for form validation
const agreementFormSchema = z.object({
  item_desc: z.string().min(1, 'Item description is required'),
  serial: z.string().optional(),
  total_price: z.number().positive('Total price must be positive'),
  deposit: z.number().min(0, 'Deposit cannot be negative'),
  admin_fee_total: z.number().min(0, 'Admin fee cannot be negative'),
  schedule_type: z.enum(['weekly', 'bi-weekly', 'monthly']),
  installment_count: z.number().int().min(1).max(120),
  due_day: z.number().int().min(1).max(31),
  grace_days: z.number().int().min(0).max(30),
  start_date: z.string().refine((date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  }, 'Start date cannot be in the past'),
});

type AgreementFormData = z.infer<typeof agreementFormSchema>;

interface AgreementWizardProps {
  onSubmit: (data: AgreementFormData, schedule: InstallmentCalculationResult, signatureData: string | null) => void;
  isLoading?: boolean;
}

export const AgreementWizard: React.FC<AgreementWizardProps> = ({ onSubmit, isLoading = false }) => {
  const [useArabicNumerals, setUseArabicNumerals] = useState(true);
  const [previewSchedule, setPreviewSchedule] = useState<InstallmentCalculationResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    getValues,
  } = useForm<AgreementFormData>({
    resolver: zodResolver(agreementFormSchema),
    defaultValues: {
      item_desc: '',
      serial: '',
      total_price: 0,
      deposit: 0,
      admin_fee_total: 0,
      schedule_type: 'monthly',
      installment_count: 12,
      due_day: 1,
      grace_days: 3,
      start_date: new Date().toISOString().split('T')[0],
    },
    mode: 'onChange',
  });

  // Watch form values for real-time preview
  const watchedValues = watch();

  // Convert numbers to Arabic numerals if needed
  const formatNumber = useCallback((num: number): string => {
    if (useArabicNumerals) {
      return num.toString().replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    }
    return num.toString();
  }, [useArabicNumerals]);

  // Convert Arabic numerals back to English
  const parseNumber = useCallback((str: string): number => {
    if (useArabicNumerals) {
      const englishStr = str.replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
      return parseFloat(englishStr) || 0;
    }
    return parseFloat(str) || 0;
  }, [useArabicNumerals]);

  // Generate preview schedule
  const generatePreview = useCallback(() => {
    try {
      const values = getValues();
      const params: InstallmentCalculationParams = {
        ...values,
        start_date: new Date(values.start_date),
      };

      const validation = validateAgreementParamsWithTimezone(params);
      if (!validation.valid) {
        toast.error(validation.errors[0]);
        return;
      }

      const schedule = generateInstallments(params);
      setPreviewSchedule(schedule);
      setShowPreview(true);
      toast.success('Preview generated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate preview');
    }
  }, [getValues]);

  // Handle form submission
  const handleFormSubmit = (data: AgreementFormData) => {
    if (!previewSchedule) {
      toast.error('Please generate a preview first');
      return;
    }
    onSubmit(data, previewSchedule, signatureData);
  };

  // Calculate remaining balance for display
  const remainingBalance = watchedValues.total_price - watchedValues.deposit + watchedValues.admin_fee_total;
  const installmentAmount = remainingBalance > 0 && watchedValues.installment_count > 0 
    ? remainingBalance / watchedValues.installment_count 
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8" dir="rtl">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                معالج إنشاء الاتفاقية - Agreement Wizard
              </h1>
              <p className="text-gray-600">
                إنشاء اتفاقية جديدة مع جدول الأقساط والتوقيع الإلكتروني
              </p>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              <button
                type="button"
                onClick={() => setUseArabicNumerals(!useArabicNumerals)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {useArabicNumerals ? 'English Numbers' : 'الأرقام العربية'}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <FileText className="w-5 h-5 text-blue-600 ml-2" />
                  <h2 className="text-lg font-semibold text-gray-900">معلومات السلعة</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      وصف السلعة *
                    </label>
                    <Controller
                      name="item_desc"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="مثال: هاتف ذكي - iPhone 15"
                        />
                      )}
                    />
                    {errors.item_desc && (
                      <p className="text-red-600 text-sm mt-1">{errors.item_desc.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الرقم التسلسلي
                    </label>
                    <Controller
                      name="serial"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="اختياري"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <CreditCard className="w-5 h-5 text-green-600 ml-2" />
                  <h2 className="text-lg font-semibold text-gray-900">المعلومات المالية</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      السعر الإجمالي *
                    </label>
                    <Controller
                      name="total_price"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.00"
                          onChange={(e) => {
                            const value = parseNumber(e.target.value);
                            field.onChange(value);
                          }}
                          value={formatNumber(field.value)}
                        />
                      )}
                    />
                    {errors.total_price && (
                      <p className="text-red-600 text-sm mt-1">{errors.total_price.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الدفعة المقدمة
                    </label>
                    <Controller
                      name="deposit"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.00"
                          onChange={(e) => {
                            const value = parseNumber(e.target.value);
                            field.onChange(value);
                          }}
                          value={formatNumber(field.value)}
                        />
                      )}
                    />
                    {errors.deposit && (
                      <p className="text-red-600 text-sm mt-1">{errors.deposit.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      رسوم الإدارة
                    </label>
                    <Controller
                      name="admin_fee_total"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.00"
                          onChange={(e) => {
                            const value = parseNumber(e.target.value);
                            field.onChange(value);
                          }}
                          value={formatNumber(field.value)}
                        />
                      )}
                    />
                    {errors.admin_fee_total && (
                      <p className="text-red-600 text-sm mt-1">{errors.admin_fee_total.message}</p>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">المبلغ المتبقي:</span>
                      <span className="font-semibold text-lg text-green-600 mr-2">
                        {formatCurrencyForDisplay(remainingBalance)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">مبلغ القسط:</span>
                      <span className="font-semibold text-lg text-blue-600 mr-2">
                        {formatCurrencyForDisplay(installmentAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Schedule */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <Calendar className="w-5 h-5 text-purple-600 ml-2" />
                  <h2 className="text-lg font-semibold text-gray-900">جدول الدفع</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      نوع الجدول *
                    </label>
                    <Controller
                      name="schedule_type"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="weekly">أسبوعي</option>
                          <option value="bi-weekly">كل أسبوعين</option>
                          <option value="monthly">شهري</option>
                        </select>
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      عدد الأقساط *
                    </label>
                    <Controller
                      name="installment_count"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          min="1"
                          max="120"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onChange={(e) => {
                            const value = parseNumber(e.target.value);
                            field.onChange(value);
                          }}
                          value={formatNumber(field.value)}
                        />
                      )}
                    />
                    {errors.installment_count && (
                      <p className="text-red-600 text-sm mt-1">{errors.installment_count.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      يوم الاستحقاق *
                    </label>
                    <Controller
                      name="due_day"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          min="1"
                          max="31"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onChange={(e) => {
                            const value = parseNumber(e.target.value);
                            field.onChange(value);
                          }}
                          value={formatNumber(field.value)}
                        />
                      )}
                    />
                    {errors.due_day && (
                      <p className="text-red-600 text-sm mt-1">{errors.due_day.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      أيام السماح
                    </label>
                    <Controller
                      name="grace_days"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          min="0"
                          max="30"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onChange={(e) => {
                            const value = parseNumber(e.target.value);
                            field.onChange(value);
                          }}
                          value={formatNumber(field.value)}
                        />
                      )}
                    />
                    {errors.grace_days && (
                      <p className="text-red-600 text-sm mt-1">{errors.grace_days.message}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تاريخ البدء *
                  </label>
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    )}
                  />
                  {errors.start_date && (
                    <p className="text-red-600 text-sm mt-1">{errors.start_date.message}</p>
                  )}
                </div>
              </div>

              {/* Signature Capture */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <PenTool className="w-5 h-5 text-indigo-600 ml-2" />
                  <h2 className="text-lg font-semibold text-gray-900">التوقيع الإلكتروني</h2>
                </div>
                
                <SignatureCapture
                  onSignatureChange={setSignatureData}
                  isRequired={true}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={generatePreview}
                  disabled={!isValid}
                  className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Calculator className="w-5 h-5 ml-2" />
                  إنشاء المعاينة
                </button>

                <button
                  type="submit"
                  disabled={!isValid || !previewSchedule || !signatureData || isLoading}
                  className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 ml-2" />
                      إنشاء الاتفاقية
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
              <div className="flex items-center mb-4">
                <Calculator className="w-5 h-5 text-orange-600 ml-2" />
                <h2 className="text-lg font-semibold text-gray-900">معاينة الأقساط</h2>
              </div>

              {!showPreview ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">اضغط على "إنشاء المعاينة" لرؤية جدول الأقساط</p>
                </div>
              ) : previewSchedule ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">ملخص الاتفاقية</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>إجمالي المبلغ:</span>
                        <span className="font-semibold">{formatCurrencyForDisplay(previewSchedule.total_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>المبلغ المتبقي:</span>
                        <span className="font-semibold">{formatCurrencyForDisplay(previewSchedule.remaining_balance)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>عدد الأقساط:</span>
                        <span className="font-semibold">{previewSchedule.installments.length}</span>
                      </div>
                      {previewSchedule.next_due_date && (
                        <div className="flex justify-between">
                          <span>تاريخ الاستحقاق التالي:</span>
                          <span className="font-semibold">{formatDateForDisplay(previewSchedule.next_due_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Installments List */}
                  <div className="max-h-96 overflow-y-auto">
                    <h3 className="font-semibold text-gray-900 mb-3">جدول الأقساط</h3>
                    <div className="space-y-2">
                      {previewSchedule.installments.slice(0, 6).map((installment) => (
                        <div key={installment.seq} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">القسط {installment.seq}</span>
                            <span className="text-sm font-semibold">{formatCurrencyForDisplay(installment.amount_due)}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDateForDisplay(installment.due_date)}
                          </div>
                        </div>
                      ))}
                      {previewSchedule.installments.length > 6 && (
                        <div className="text-center py-2 text-sm text-gray-500">
                          + {previewSchedule.installments.length - 6} أقساط أخرى
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status Indicators */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full ml-2"></div>
                      <span>مدفوع: {previewSchedule.paid_count}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full ml-2"></div>
                      <span>متأخر: {previewSchedule.overdue_count}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-500">حدث خطأ في إنشاء المعاينة</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 